import { useEffect, useState } from "react"
import { SpinnerText } from "../components/ui/Spinner"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { useSelector } from "react-redux"
import AppLayout from "../layout/AppLayout"
import ProgressSection from "../components/trademark/progress/ProgressSection"
import { getApplicationById } from "../services/applicationService"
import {
  confirmStripeCheckoutSession,
  createStripeCheckoutSession,
  submitInvoicePayment
} from "../services/stripePaymentService"
import { formatApiError } from "../utils/formatApiError"
import { RootState } from "../store/store"
import FeeSummary from "../components/ui/FeeSummary"
import ServiceDescriptions from "../components/ui/ServiceDescriptions"
import { calculateTrademarkFees } from "../utils/applicationFees"

export default function PaymentPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const isAdmin = useSelector((state: RootState) => Boolean(state.auth.user?.isAdmin))
  const paymentResult = new URLSearchParams(location.search).get("payment")
  const stripeSessionId = new URLSearchParams(location.search).get("session_id")

  const [loading, setLoading] = useState(true)
  const [application, setApplication] = useState<any | null>(null)

  const [confirmed, setConfirmed] = useState(false)

  const [showConfirm, setShowConfirm] = useState(false)
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(
    false
  )
  const [showInvoiceSuccess, setShowInvoiceSuccess] = useState(false)

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) {
      setLoading(false)
      return
    }

    async function load(applicationId: string) {
      try {
        const app = await getApplicationById(applicationId)

        const canViewPaymentPage =
          app.status === "VALIDATED" ||
          app.status === "PAYMENT_PENDING" ||
          app.status === "AWAITING_RELEASE" ||
          app.status === "PAID" ||
          app.status === "ERROR" ||
          paymentResult === "success" ||
          isAdmin

        if (!canViewPaymentPage) {
          navigate(`/applications/${applicationId}/edit`)
          return
        }

        setApplication(app)
      } finally {
        setLoading(false)
      }
    }

    load(id)
  }, [id, navigate, paymentResult, isAdmin])

  useEffect(() => {
    if (paymentResult !== "success" || !stripeSessionId) return

    async function confirmPayment(sessionId: string) {
      try {
        await confirmStripeCheckoutSession(sessionId)
        setShowPaymentSuccess(true)
      } catch (confirmError) {
        setError(
          formatApiError(
            confirmError,
            "The payment was completed, but the application status could not be updated. Please reload the page and try again."
          )
        )
      }
    }

    confirmPayment(stripeSessionId)
  }, [paymentResult, stripeSessionId])

  if (loading) {
    return (
      <AppLayout title="Payment">
        <div className="flex justify-center py-32 text-gray-600">
          <SpinnerText>Loading application…</SpinnerText>
        </div>
      </AppLayout>
    )
  }

  if (!application || !id) {
    return (
      <AppLayout title="Payment">
        <div className="flex justify-center py-32 text-gray-600">
          Application not found.
        </div>
      </AppLayout>
    )
  }

  const applicationStatus = String(application.status || "")
  const paymentIsPaid = applicationStatus === "PAID"
  const userPaymentIsLocked =
    applicationStatus === "PAYMENT_PENDING" ||
    applicationStatus === "AWAITING_RELEASE" ||
    applicationStatus === "PAID" ||
    applicationStatus === "ERROR"
  const canContinue = confirmed && (!userPaymentIsLocked || isAdmin)
  const feeBreakdown = calculateTrademarkFees({
    goodsAndServices: application.goodsAndServices?.entries || [],
    priorityData: application.priorities || [],
    seniorityData: application.seniorities || []
  })

  async function handleSubmit() {
    if (!id) return
    if (userPaymentIsLocked && !isAdmin) return

    setSubmitting(true)
    setError(null)

    try {
      const response = await createStripeCheckoutSession({
        applicationType: "trademark",
        applicationId: id
      })

      window.location.href = response.url
    } catch (submitError) {
      setError(
        formatApiError(
          submitError,
          "The payment could not be started. Please reload the page and try again."
        )
      )
    } finally {
      setSubmitting(false)
    }
  }

  async function handleInvoiceSubmit() {
    if (!id) return
    if (userPaymentIsLocked && !isAdmin) return

    setSubmitting(true)
    setError(null)

    try {
      await submitInvoicePayment({
        applicationType: "trademark",
        applicationId: id
      })
      setShowConfirm(false)
      setShowInvoiceSuccess(true)
    } catch (submitError) {
      setError(
        formatApiError(
          submitError,
          "The invoice submission could not be started. Please reload the page and try again."
        )
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AppLayout title="Payment: Review application">
      <div className="sticky top-0 z-30 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <ProgressSection
            complete={{
              languages: true,
              trademark: true,
              goods: true,
              applicant: true,
              signature: true,
              payment: paymentIsPaid
            }}
          />
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10 pb-36">
        <h1 className="text-xl font-semibold text-gray-800 mb-6">
          Review your application
        </h1>

        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="divide-y divide-gray-200">
            {(paymentResult === "cancelled" ||
              applicationStatus === "PAYMENT_PENDING" ||
              applicationStatus === "AWAITING_RELEASE" ||
              applicationStatus === "PAID" ||
              applicationStatus === "ERROR") && (
              <div className="space-y-3 p-6 sm:p-8">
                {paymentResult === "cancelled" && (
                  <div className="rounded border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
                    Payment was cancelled. You can start the payment again when you are ready.
                  </div>
                )}

                {applicationStatus === "AWAITING_RELEASE" && (
                  <div className="rounded border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                    {isAdmin
                      ? "Waiting for release. The attorney reviews this application and files it afterwards."
                      : "Thank you. Your application is being reviewed by our attorney and will be filed afterwards. You will receive a confirmation with the invoice by e-mail, and the filing report once it has been filed."}
                  </div>
                )}

                {applicationStatus === "PAYMENT_PENDING" && (
                  <div className="rounded border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                    Payment is pending. The application has been submitted for bank transfer payment.
                  </div>
                )}

                {applicationStatus === "PAID" && (
                  <div className="rounded border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                    Payment is completed. This application has already been submitted.
                  </div>
                )}

                {applicationStatus === "ERROR" && (
                  <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                    Submission failed. An admin can review and submit the application again.
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 gap-x-12 gap-y-6 p-6 sm:grid-cols-2 sm:p-8">
              <Field label="Reference number">
                {application.referenceNumber?.referenceNumber || "—"}
              </Field>

              <Field label="Trademark">
                <Bullets
                  items={[
                    application.markId?.type &&
                      `Type: ${application.markId.type}`,
                    application.markId?.word &&
                      `Word: ${application.markId.word}`,
                    application.markId?.text &&
                      `Text: ${application.markId.text}`
                  ]}
                />
              </Field>

              <Field label="Languages">
                <Bullets
                  items={[
                    `Filing language: ${application.languages?.filingLanguage || "—"}`,
                    `Second language: ${application.languages?.secondLanguage || "—"}`
                  ]}
                />
              </Field>
            </div>

            <div className="p-6 sm:p-8">
              <div className="mb-4 text-sm font-semibold text-gray-900">
                Goods &amp; Services
              </div>

              {application.goodsAndServices?.entries?.length ? (
                <div className="divide-y divide-gray-100">
                  {application.goodsAndServices.entries.map((e: any) => (
                    <div
                      key={e.classNumber}
                      className="flex gap-4 py-3 first:pt-0 last:pb-0"
                    >
                      <span className="h-fit shrink-0 rounded-md bg-blue-800 px-2.5 py-1 text-xs font-semibold text-white">
                        Class {e.classNumber}
                      </span>
                      <p className="text-justify text-sm leading-relaxed text-gray-600">
                        {e.terms.join(", ")}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500">—</div>
              )}
            </div>

            <div className="grid grid-cols-1 gap-x-12 gap-y-6 p-6 sm:grid-cols-2 sm:p-8">
              <Field label="Applicants">
                {application.applicants?.length ? (
                  application.applicants.map((a: any, i: number) => (
                    <div key={i}>
                      {a.type === "natural"
                        ? `${a.firstName} ${a.lastName}`
                        : a.legalName}
                    </div>
                  ))
                ) : (
                  <div>—</div>
                )}
              </Field>

              <Field label="Signature">
                <Bullets
                  items={[
                    `Name: ${application.signature?.signerName || "—"}`,
                    `Capacity: ${application.signature?.signerCapacity || "—"}`
                  ]}
                />
              </Field>

              {application.priorities?.length > 0 && (
                <Field label="Priorities">
                  {application.priorities.map((p: any, i: number) => (
                    <div key={i}>
                      {p.office} - {p.filingNumber} (
                      {p.filingDate
                        ? new Date(p.filingDate).toLocaleDateString()
                        : "—"}
                      )
                    </div>
                  ))}
                </Field>
              )}

              {application.seniorities?.length > 0 && (
                <Field label="Seniorities">
                  {application.seniorities.map((s: any, i: number) => (
                    <div key={i}>
                      {s.countryCode} - {s.registrationNumber}
                    </div>
                  ))}
                </Field>
              )}
            </div>

            <div className="px-6 pb-6 sm:px-8 sm:pb-8">
              <ServiceDescriptions type="trademark" />
            </div>
            <FeeSummary
              serviceLines={feeBreakdown.serviceLines}
              officialLines={feeBreakdown.officialLines}
              subtotalServices={feeBreakdown.subtotalServices}
              subtotalOfficial={feeBreakdown.subtotalOfficial}
              total={feeBreakdown.total}
            />

            <div className="p-6 sm:p-8">
              <label className="flex items-start gap-3 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-blue-800"
                />
                I confirm that all information provided above is correct and complete.
              </label>
            </div>

            <div className="p-6 sm:p-8">
              <div
                className={`border border-gray-300 rounded-xl p-6 space-y-4 ${
                  !confirmed || (userPaymentIsLocked && !isAdmin)
                    ? "bg-gray-100 text-gray-400"
                    : "bg-white"
                }`}
              >
                <div className="text-sm font-medium text-gray-800">
                  Payment
                </div>
                <p className="text-sm">
                  {userPaymentIsLocked && !isAdmin
                    ? "No further payment action is required for this application."
                    : "You can pay the full filing amount now by Stripe or receive an invoice for bank transfer."}
                </p>
              </div>

            </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 z-40 flex w-full items-center justify-between gap-4 border-t bg-gray-100 px-6 py-3 shadow-sm">
        <p className="text-sm text-gray-700">
          Review your application and fees before continuing to payment.
        </p>
        <div className="flex shrink-0 gap-3">
          <button
            type="button"
            onClick={() => navigate("/applications")}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canContinue}
            onClick={() => setShowConfirm(true)}
            className={`rounded-lg px-4 py-2 text-sm font-medium text-white transition ${
              canContinue
                ? "bg-dantzOrange hover:brightness-110 active:scale-[0.98]"
                : "cursor-not-allowed bg-gray-300"
            }`}
          >
            Continue
          </button>
        </div>
      </div>

      {showConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-4">
            <h3 className="text-lg font-semibold">Choose payment method</h3>

            <p className="text-sm text-gray-700">
              Pay now with Stripe, or receive an invoice for a bank transfer. In both cases our attorney reviews your application and files it afterwards.
            </p>

            {error && <div className="text-sm text-red-600">{error}</div>}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 text-sm border rounded"
                disabled={submitting}
              >
                Cancel
              </button>

              <button
                onClick={handleInvoiceSubmit}
                disabled={submitting}
                className="px-4 py-2 text-sm border rounded font-medium disabled:cursor-not-allowed disabled:opacity-60"
              >
                Send invoice
              </button>

              <button
                onClick={handleSubmit}
                disabled={submitting}
                className={`px-4 py-2 text-sm rounded font-medium transition ${
                  submitting
                    ? "bg-orange-300 text-white cursor-not-allowed"
                    : "bg-orange-500 text-white hover:bg-orange-600"
                }`}
              >
                {submitting ? <SpinnerText>Processing…</SpinnerText> : "Pay with Stripe"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showInvoiceSuccess && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Invoice request received
            </h3>

            <p className="text-sm text-gray-700">
              Your application was received. It will be reviewed by our attorney and filed afterwards. You will receive a confirmation with our invoice by e-mail. The status will remain payment pending until the payment is received and confirmed.
            </p>

            <div className="flex justify-end pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowInvoiceSuccess(false)
                  navigate("/home")
                }}
                className="px-4 py-2 text-sm rounded bg-orange-500 text-white hover:bg-orange-600"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {showPaymentSuccess && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Payment received
            </h3>

            <p className="text-sm text-gray-700">
              Your payment was received. Your application will be reviewed by our attorney and filed afterwards. You will receive a confirmation with the invoice by e-mail, and the filing report once it has been filed.
            </p>

            <div className="flex justify-end pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowPaymentSuccess(false)
                  navigate("/home")
                }}
                className="px-4 py-2 text-sm rounded bg-orange-500 text-white hover:bg-orange-600"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}

function Field({
  label,
  children
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="mb-1.5 text-sm font-semibold text-gray-900">{label}</div>
      <div className="space-y-1 text-sm text-gray-600">{children}</div>
    </div>
  )
}

function Bullets({
  items
}: {
  items: (string | false | null | undefined)[]
}) {
  const visible = items.filter(Boolean) as string[]

  if (visible.length === 0) {
    return <div>—</div>
  }

  return (
    <ul className="space-y-1">
      {visible.map((item, i) => (
        <li key={i} className="flex items-start gap-2">
          <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-gray-400" />
          {item}
        </li>
      ))}
    </ul>
  )
}
